<template>
  <div id="phone_numbers" class="flex flex-col gap-6 text_color">
    <div class="flex flex-row justify-between items-center">
      <h1 class="text-xl font-semibold text_color">Phone Numbers</h1>
      <a-button
        class="primary_medium"
        @click="handleAddPhoneNumber"
        :loading="checking"
      >
        {{ checking ? "Checking Status..." : "Get New Number" }}
      </a-button>
    </div>

    <div
      class="flex flex-row items-baseline gap-4 p-3 rounded-lg bg_2 border_4"
      v-if="hasCallSettingsPermission"
    >
      <div class="py-1">
        <a-switch
          size="small"
          :checked="isGlobalRecordingEnabled"
          @change="toggleRecording"
        />
      </div>

      <div class="flex flex-col gap-1">
        <span class="text-base font-semibold">Record all calls</span>
        <span class="text-[13px] font-normal secondary_color"
          >This setting enables call recording for your entire workspace.</span
        >
      </div>
    </div>

    <div class="flex flex-col">
      <SkeletonLoader v-if="isLoading" :page="loaderPages.TABLE" />

      <CustomTable
        v-else-if="phoneNumbers?.length > 0"
        empty-cell-value="--"
        :headers="phoneNumberHeaders"
        :data="phoneNumbers"
        :actions="phoneNumberActions"
        :show-actions-column="true"
        :custom-column-render="customColumnRender"
        :custom-row="customRow"
        height="calc(100vh - (310px + var(--banner-height)))"
      />

      <EmptyScreen
        v-else
        source="phone_numbers"
        text="No phone numbers generated yet!"
      />
    </div>

    <AddPhoneNumber
      source="phone_numbers"
      :drawer-visible="showAddPhoneNumberDrawer"
      @complete="handleGetPhoneNumber"
      @update:model-value="showAddPhoneNumberDrawer = false"
    />

    <UpdatePhoneNumberDetails
      source="phone_numbers"
      :drawer-visible="showEditPhoneNumberDrawer"
      :data="editPhoneNumberData"
      @update:model-value="showEditPhoneNumberDrawer = false"
    />

    <CustomModal
      title="Delete Phone Number"
      v-model:visible="showModalToDelete"
      width="30vw"
    >
      <span class="text-base text_color leading-5"
        >Are you sure you want to delete
        {{ numberToManipulate?.phone_number }} number?</span
      >
      <template #footer>
        <a-button class="primary_medium" @click="toDeletePhoneNumber"
          >Confirm
        </a-button>
      </template>
    </CustomModal>

    <CustomModal
      title="Manage Call Forwarding"
      v-model:visible="showModalForCallForwarding"
      width="30vw"
    >
      <div class="form_col">
        <span class="text-[13px] secondary_color"
          >Forward '{{ numberToManipulate?.phone_number }}' to</span
        >
        <a-input
          ref="forwardingInputRef"
          v-model:value="callForwardNumber"
          class="input_medium"
          placeholder="Enter phone number"
          @press-enter="toAddForwardingNumber"
          allow-clear
        />
      </div>

      <template #footer>
        <a-button
          class="primary_medium"
          :loading="isProcessing"
          :disabled="disableButton"
          @click="toAddForwardingNumber"
          >{{ isProcessing ? "Saving changes" : "Save changes" }}</a-button
        >
      </template>
    </CustomModal>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, nextTick, onUnmounted } from "vue";
import { message } from "ant-design-vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import { loaderPages } from "@/src/constants/loaderConstants.js";
import { telephonyProviderGenericWrapper } from "@/src/constants/phoneNumberConstants.js";
import { callSettingKeys } from "@/src/constants/callConstants.js";
import {
  toGetPhoneNumbers,
  validatePhoneNumber,
} from "@/src/utils/callsUtils.js";
import {
  validationEntities,
  getPhoneNumberHeaders,
} from "@/src/constants/callConstants.js";
import store from "@/src/store/index.js";
import eventBus from "@/src/utils/eventBus.js";
import CallService from "@/src/api/CallService.js";
import ProviderManager from "@/src/services/telephony/providerManager.js";
import EmptyScreen from "@/src/components/EmptyScreen.vue";
import CustomModal from "@/src/components/CustomModal.vue";
import SkeletonLoader from "@/src/components/SkeletonLoader.vue";
import AddPhoneNumber from "@/src/views/calls/phone_number/AddPhoneNumber.vue";
import UpdatePhoneNumberDetails from "@/src/views/calls/phone_number/UpdatePhoneNumberDetails.vue";
import CustomTable from "@/src/components/custom_table_components/CustomTable.vue";
import CallForwardingToggle from "@/src/components/telephony_components/toggles/CallForwardingToggle.vue";

const checking = ref(false);
const showAddPhoneNumberDrawer = ref(false);
const showEditPhoneNumberDrawer = ref(false);
const editPhoneNumberData = ref(null);
const phoneNumbers = ref(null);
const numberToManipulate = ref(null);
const showModalToDelete = ref(false);
const showModalForCallForwarding = ref(false);
const callForwardNumber = ref("");
const isLoading = ref(false);
const isProcessing = ref(false);
const forwardingInputRef = ref(null);


const phoneNumberHeaders = computed(() => getPhoneNumberHeaders());

const phoneNumberActions = computed(() => {
  try {
    const actions = [];

    actions.push({
      label: "Edit",
      handler: (row) => handleEditNumber(row),
      icon: imageNames.EDIT,
    });

    actions.push({
      label: "Delete",
      handler: (row) => handleDeleteNumber(row),
      icon: imageNames.DELETE_GREY,
    });

    return actions;
  } catch (error) {
    console.error(
      "An error occurred while computing phone number actions: ",
      error,
    );
    return [];
  }
});

const isGlobalRecordingEnabled = computed(
  () => store.getters["dialerSettings/isGlobalRecordingEnabled"],
);

const customColumnRender = {
  is_forwarding_enabled: CallForwardingToggle,
};

const hasCallSettingsPermission = computed(() => {
  const permissions = JSON.parse(
    localStorage.getItem("userPermissions") || "[]",
  );
  return permissions.includes("call_settings");
});


const handleEditNumber = (row) => {
  numberToManipulate.value = row;
  editPhoneNumberData.value = row;
  showEditPhoneNumberDrawer.value = true;
};

const handleDeleteNumber = (row) => {
  numberToManipulate.value = row;
  showModalToDelete.value = true;
};

const customRow = (record) => {
  try { 
    return {
      onClick: () => handleEditNumber(record),
      style: { cursor: "pointer" },
    };
  } catch (error) {
    console.error("An error occurred while custom row: ", error);
    return {};
  }
};

const handleCallForwarding = (row) => {
  callForwardNumber.value = row?.forwarding_number;
  numberToManipulate.value = row;
  showModalForCallForwarding.value = true;

  nextTick(() => {
    forwardingInputRef.value?.focus();
  });
};

const handleGetPhoneNumber = (trigger) => {
  if (trigger) eventBus.emit("initialise-sdk");
  getPhoneNumbers();
};

const disableButton = computed(() => {
  return callForwardNumber.value === "";
});

const handleAddPhoneNumber = async () => {
  showAddPhoneNumberDrawer.value = true;
};

const getPhoneNumbers = async (loader = true) => {
  try {
    if (loader) isLoading.value = true;
    phoneNumbers.value = await toGetPhoneNumbers();
  } catch (error) {
    console.error("An error occurred while calling get phone number: ", error);
  } finally {
    isLoading.value = false;
  }
};

const toDeletePhoneNumber = async () => {
  try {
    const params = {
      id: numberToManipulate.value?.id,
      provider:
        telephonyProviderGenericWrapper[
          ProviderManager.getProviderForNumber(
            numberToManipulate.value?.phone_number,
          )
        ],
    };

    const response = await CallService.deletePhoneNumber(params);
    if (response?.success) {
      await getPhoneNumbers();
      message.success(
        "Number deleted successfully! All campaigns under this phone number have been ended. ",
      );
    }
  } catch (error) {
    console.error("An error occurred while deleting the phone number: ", error);
    if (error.response?.data?.error) {
      message.error(error.response?.data?.error);
    }
  } finally {
    showModalToDelete.value = false;
  }
};

const toAddForwardingNumber = async () => {
  try {
    isProcessing.value = true;

    const isValid = validatePhoneNumber(
      callForwardNumber.value,
      validationEntities.NORMAL_CALL,
    );
    if (!isValid?.valid) {
      message.error("Please enter a valid phone number");
      return;
    }

    const payload = {
      number: callForwardNumber.value,
    };
    const response = await CallService.addForwardingNumber(
      numberToManipulate.value.id,
      payload,
    );

    if (response?.success) {
      showModalForCallForwarding.value = false;
      message.success("Forwarding Number added successfully!!");
      await getPhoneNumbers();
    }
  } catch (error) {
    showModalForCallForwarding.value = false;
    message.error("An error occurred while adding Forwarding Number!!");
    console.log("An error occurred while adding forwarding number: ", error);
  } finally {
    isProcessing.value = false;
  }
};

const toggleRecording = async () => {
  const isConsentGiven = store.getters["calls/recordingConsentStatus"];
  const newStatus = !isGlobalRecordingEnabled.value;

  if (newStatus && !isConsentGiven) {
    eventBus.emit("open-consent-form", true);
    const unwatch = store.watch(
      () => store.getters["calls/recordingConsentStatus"],
      async (updatedConsent) => {
        if (updatedConsent) {
          unwatch();
          await updateRecordingStatus(true);
        }
      },
    );
  } else {
    await updateRecordingStatus(newStatus);
  }
};

const updateRecordingStatus = async (newStatus) => {
  try {
    const response = await store.dispatch(
      "dialerSettings/updateSetting",
      { key: callSettingKeys.RECORD_CALL, value: newStatus },
    );
    if (response?.success) {
      message.success(
        `Recording status set to ${newStatus ? "enabled" : "disabled"}`,
      );
    } else {
      message.error("Failed to update recording status. Please try again.");
    }
  } catch (error) {
    message.error("An error occurred while updating recording status.");
    console.error("Error updating global recording setting:", error);
  }
};

onMounted(() => {
  getPhoneNumbers();
  eventBus.on("forwarding-toggle", () => handleCallForwarding());
  eventBus.on("refresh-phone-numbers", () => getPhoneNumbers(false));
});

onUnmounted(() => {
  eventBus.off("forwarding-toggle", () => handleCallForwarding());
  eventBus.off("refresh-phone-numbers", () => getPhoneNumbers(false));
});
</script>
